import { useState, useEffect } from "react";
import axios from "axios";
import { 
  MapPin, 
  Sun, 
  Umbrella, 
  Users, 
  Star,
  Sparkles,
  Phone,
  Navigation,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  Filter,
  Waves
} from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";
import { API } from "../lib/api";

// Section icons mapping
const SECTION_ICONS = {
  sparkles: Sparkles,
  sun: Sun,
  umbrella: Umbrella,
  users: Users,
  star: Star
};

export default function BeachesPage() {
  const [curatedData, setCuratedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedSection, setExpandedSection] = useState("best_nearby");
  const { language } = useLanguage();
  const { location, locationData } = useLocation();

  const translations = {
    en: {
      title: "Beaches",
      subtitle: "Curated beach guide for",
      allBeaches: "All beaches",
      freeBeaches: "Free beaches",
      beachClubs: "Beach clubs",
      familyFriendly: "Family",
      premiumClubs: "Premium",
      whyWeRecommend: "Why we recommend it",
      localTip: "Local tip",
      getDirections: "Get directions",
      contact: "Contact",
      free: "Free",
      sandy: "Sandy",
      rocky: "Rocky",
      mixed: "Mixed",
      showMore: "Show more",
      showLess: "Show less",
      perDay: "/day",
      crowdLow: "Quiet",
      crowdModerate: "Moderate",
      crowdHigh: "Busy"
    },
    it: {
      title: "Spiagge",
      subtitle: "Guida curata alle spiagge di",
      allBeaches: "Tutte le spiagge",
      freeBeaches: "Spiagge libere",
      beachClubs: "Lidi",
      familyFriendly: "Famiglie",
      premiumClubs: "Premium",
      whyWeRecommend: "Perché la consigliamo",
      localTip: "Consiglio locale",
      getDirections: "Indicazioni",
      contact: "Contatta",
      free: "Gratuita",
      sandy: "Sabbiosa",
      rocky: "Rocciosa",
      mixed: "Mista",
      showMore: "Mostra di più",
      showLess: "Mostra meno",
      perDay: "/giorno",
      crowdLow: "Tranquilla",
      crowdModerate: "Moderata",
      crowdHigh: "Affollata"
    }
  };

  const txt = translations[language] || translations.en;

  const filters = [
    { id: "all", label: txt.allBeaches, icon: Waves },
    { id: "free", label: txt.freeBeaches, icon: Sun },
    { id: "paid", label: txt.beachClubs, icon: Umbrella },
    { id: "family", label: txt.familyFriendly, icon: Users },
    { id: "premium", label: txt.premiumClubs, icon: Star }
  ];

  useEffect(() => {
    fetchCuratedBeaches();
  }, [location]);

  const fetchCuratedBeaches = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/beaches/curated?location=${location || 'monopoli'}`);
      setCuratedData(res.data);
    } catch (error) {
      console.error("Error fetching beaches:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = (beach) => {
    const message = encodeURIComponent(`Hi! I'm interested in ${beach.name}. Can you help me with more info?`);
    const phone = beach.contact || locationData?.whatsapp || '393331234567';
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const getCrowdBadge = (level) => {
    const colors = {
      low: "bg-emerald-100 text-emerald-700",
      moderate: "bg-amber-100 text-amber-700",
      high: "bg-red-100 text-red-700"
    };
    const labels = {
      low: txt.crowdLow,
      moderate: txt.crowdModerate,
      high: txt.crowdHigh
    };
    return { color: colors[level] || colors.moderate, label: labels[level] || level };
  };

  const getTypeLabel = (type) => {
    if (type === "free") return { label: txt.free, color: "bg-emerald-500" };
    if (type === "paid") return { label: txt.beachClubs, color: "bg-blue-500" };
    return { label: txt.mixed, color: "bg-purple-500" };
  };

  const getSurfaceLabel = (surface) => {
    const labels = { sandy: txt.sandy, rocky: txt.rocky, mixed: txt.mixed };
    return labels[surface] || surface;
  };

  const getFilteredSections = () => {
    if (!curatedData?.sections) return {};
    
    if (activeFilter === "all") return curatedData.sections;
    if (activeFilter === "free") return { free_beaches: curatedData.sections.free_beaches };
    if (activeFilter === "paid") return { beach_clubs: curatedData.sections.beach_clubs };
    if (activeFilter === "family") return { family_friendly: curatedData.sections.family_friendly };
    if (activeFilter === "premium") return { premium_clubs: curatedData.sections.premium_clubs };
    
    return curatedData.sections;
  };

  const BeachCard = ({ beach, compact = false }) => {
    const typeInfo = getTypeLabel(beach.type);
    const crowdInfo = getCrowdBadge(beach.crowd_level);
    
    return (
      <article 
        className={`bg-white rounded-2xl overflow-hidden border border-sand-300 shadow-sm ${compact ? '' : 'hover:shadow-lg hover:shadow-olive-600/10 transition-shadow'}`}
        data-testid="beach-card"
      >
        {/* Image Carousel */}
        <div className="relative h-48 overflow-hidden">
          <img 
            src={beach.images?.[0] || '/placeholder-beach.jpg'} 
            alt={beach.name}
            className="w-full h-full object-cover"
          />
          {/* Type Badge */}
          <div className={`absolute top-3 left-3 ${typeInfo.color} text-white text-xs font-semibold px-2.5 py-1 rounded-full`}>
            {typeInfo.label}
          </div>
          {/* Price Badge (if paid) */}
          {beach.price_info && (
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-stone-800 text-xs font-semibold px-2.5 py-1 rounded-full">
              {beach.price_info}
            </div>
          )}
          {/* More images indicator */}
          {beach.images?.length > 1 && (
            <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
              +{beach.images.length - 1}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-semibold text-stone-900 text-lg">{beach.name}</h3>
              <p className="text-sm text-stone-500 flex items-center gap-1">
                <MapPin size={12} />
                {beach.area}, {beach.town}
              </p>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className={`text-xs px-2 py-0.5 rounded-full ${crowdInfo.color}`}>
              {crowdInfo.label}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-sand-200 text-stone-600">
              {getSurfaceLabel(beach.beach_surface)}
            </span>
            {beach.tags?.slice(0, 2).map((tag, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-olive-100 text-olive-700 capitalize">
                {tag}
              </span>
            ))}
          </div>

          {/* Why we recommend */}
          <div className="mb-3">
            <p className="text-xs text-olive-600 font-semibold uppercase tracking-wide mb-1">
              {txt.whyWeRecommend}
            </p>
            <p className="text-sm text-stone-700">
              {beach.why_we_recommend?.[language] || beach.why_we_recommend?.en}
            </p>
          </div>

          {/* Local tip */}
          {beach.local_tip && (
            <div className="bg-amber-50 rounded-xl p-3 mb-3">
              <p className="text-xs text-amber-700 font-semibold mb-1">{txt.localTip}</p>
              <p className="text-sm text-amber-900">
                {beach.local_tip?.[language] || beach.local_tip?.en}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <a
              href={beach.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-sand-200 hover:bg-sand-300 text-stone-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
            >
              <Navigation size={14} />
              {txt.getDirections}
            </a>
            <button
              onClick={() => handleWhatsApp(beach)}
              className="flex-1 bg-olive-600 hover:bg-olive-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
            >
              <MessageCircle size={14} />
              {txt.contact}
            </button>
          </div>
        </div>
      </article>
    );
  };

  const BeachSection = ({ sectionKey, section }) => {
    const isExpanded = expandedSection === sectionKey;
    const Icon = SECTION_ICONS[section.icon] || Waves;
    const beaches = section.beaches || [];
    const displayBeaches = isExpanded ? beaches : beaches.slice(0, 3);

    return (
      <section className="mb-8" data-testid={`section-${sectionKey}`}>
        <button
          onClick={() => setExpandedSection(isExpanded ? null : sectionKey)}
          className="w-full flex items-center justify-between mb-4 group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-olive-100 rounded-xl flex items-center justify-center">
              <Icon size={20} className="text-olive-600" />
            </div>
            <div className="text-left">
              <h2 className="font-semibold text-stone-800">
                {section.title?.[language] || section.title?.en}
              </h2>
              <p className="text-xs text-stone-500">{beaches.length} beaches</p>
            </div>
          </div>
          <ChevronDown 
            size={20} 
            className={`text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {displayBeaches.map((beach) => (
            <BeachCard key={beach.id} beach={beach} />
          ))}
        </div>

        {beaches.length > 3 && (
          <button
            onClick={() => setExpandedSection(isExpanded ? null : sectionKey)}
            className="mt-4 w-full py-2 text-olive-600 hover:text-olive-700 text-sm font-medium flex items-center justify-center gap-1"
          >
            {isExpanded ? txt.showLess : `${txt.showMore} (${beaches.length - 3})`}
            <ChevronRight size={16} className={isExpanded ? 'rotate-90' : ''} />
          </button>
        )}
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-sand-200" data-testid="beaches-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-sand-300 via-sand-200 to-sea-100 text-stone-900 px-6 pt-8 pb-6 border-b border-sand-300">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-serif text-stone-800">{txt.title}</h1>
          <p className="text-stone-500 text-sm mt-2">{txt.subtitle} {locationData?.name}</p>
        </div>
      </header>

      {/* Filter Tabs */}
      <div className="sticky top-0 z-20 bg-sand-100/95 backdrop-blur-sm border-b border-sand-300 px-4 py-3">
        <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto scrollbar-hide">
          {filters.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => {
                  setActiveFilter(f.id);
                  setExpandedSection(f.id === "all" ? "best_nearby" : f.id === "free" ? "free_beaches" : f.id === "paid" ? "beach_clubs" : f.id === "family" ? "family_friendly" : "premium_clubs");
                }}
                data-testid={`filter-${f.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === f.id 
                    ? "bg-olive-600 text-white shadow-md shadow-olive-600/20" 
                    : "bg-white text-stone-600 hover:bg-sand-200 border border-sand-300"
                }`}
              >
                <Icon size={14} />
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-4xl mx-auto pb-24 md:pb-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-olive-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            {curatedData && (
              <div className="flex gap-4 mb-6">
                <div className="flex-1 bg-olive-100 rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl font-bold text-olive-700">{curatedData.total_free}</p>
                  <p className="text-xs text-olive-600">{txt.freeBeaches}</p>
                </div>
                <div className="flex-1 bg-sea-100 rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl font-bold text-sea-700">{curatedData.total_paid}</p>
                  <p className="text-xs text-sea-600">{txt.beachClubs}</p>
                </div>
              </div>
            )}

            {/* Sections */}
            {Object.entries(getFilteredSections()).map(([key, section]) => (
              <BeachSection key={key} sectionKey={key} section={section} />
            ))}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
